import React, { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import {
  ArrowLeft,
  Calendar,
  Clock,
  Share2,
  Linkedin,
  Facebook,
  Loader2,
  X,
  Link2,
  Check,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

import api from "../lib/api";
import {
  WhatWeDoSection,
  IndustriesSection,
  EngagementModelsSection,
  TechnologiesSection,
  FAQSection,
} from "../components/CommonSections";

// Render CKEditor-authored HTML with the same content styles the editor uses
// (lists, table borders, image alignment/captions). Scoped to `.ck-content`.
import "ckeditor5/ckeditor5-content.css";

const API_HOST = process.env.REACT_APP_API_HOST || "http://127.0.0.1:8000";

// Related articles shown per page.
const RELATED_PAGE_SIZE = 6;

const imageUrl = (path) => {
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path;
  return `${API_HOST.replace(/\/$/, "")}/${String(path).replace(/^\/?/, "")}`;
};

// Plain text from HTML: strips tags AND decodes entities (&nbsp;, &amp;, …).
// DOMParser builds an inert document, so images/scripts never load or run.
const stripHtml = (html) => {
  if (!html) return "";
  const doc = new DOMParser().parseFromString(String(html), "text/html");
  return (doc.body.textContent || "").replace(/\s+/g, " ").trim();
};

const formatDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const readTimeOf = (html) => {
  const words = stripHtml(html).split(/\s+/).filter(Boolean).length;
  return `${Math.max(1, Math.round(words / 200))} min read`;
};

const parseTags = (tags) => {
  if (Array.isArray(tags)) return tags;
  if (!tags) return [];
  try {
    const parsed = JSON.parse(tags);
    if (Array.isArray(parsed)) return parsed;
  } catch (e) {
    /* not JSON */
  }
  return String(tags)
    .split(",")
    .map((t) => t.trim())
    .filter(Boolean);
};

const BlogDetailPage = () => {
  const { id } = useParams();
  const [blog, setBlog] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [relatedPage, setRelatedPage] = useState(1);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setNotFound(false);
      setRelatedPage(1);
      try {
        const { data } = await api.get(`users/blogs/${id}`);
        const found = data?.data || data?.blog || data;
        if (!found || !found.id) {
          if (!cancelled) setNotFound(true);
          return;
        }
        if (cancelled) return;
        setBlog(found);

        // Related — same category, excluding the current blog.
        if (found.blog_category) {
          try {
            const res = await api.get("users/by-category", {
              params: { category: found.blog_category },
            });
            const list = Array.isArray(res.data?.data) ? res.data.data : [];
            if (!cancelled) {
              setRelated(list.filter((r) => r.id !== found.id));
            }
          } catch (e) {
            /* related is optional */
          }
        }
      } catch (error) {
        if (!cancelled) setNotFound(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [id]);

  // Update the document <head> with this blog's title/description/image so the
  // browser tab and JS-running crawlers (e.g. Google) see per-post metadata.
  // Note: Facebook/WhatsApp/LinkedIn don't run JS — those need server-side tags.
  useEffect(() => {
    
    if (!blog) return;
    

    const metaTitle = blog.blog_title || "Infotech.Works";
    const metaDesc = stripHtml(blog.blog_description).slice(0, 160);
    const metaImage = blog.blog_image_url || imageUrl(blog.blog_image);
    const metaUrl = window.location.href;

    const prevTitle = document.title;
    document.title = metaTitle;

    const upsert = (attr, key, content) => {
      let el = document.head.querySelector(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content || "");
    };

    upsert("name", "description", metaDesc);
    upsert("property", "og:type", "article");
    upsert("property", "og:title", metaTitle);
    upsert("property", "og:description", metaDesc);
    upsert("property", "og:image", metaImage);
    upsert("property", "og:url", metaUrl);
    upsert("name", "twitter:card", "summary_large_image");
    upsert("name", "twitter:title", metaTitle);
    upsert("name", "twitter:description", metaDesc);
    upsert("name", "twitter:image", metaImage);

    // const favicon = document.head.querySelector('link[rel="icon"]');
    // const prevFavicon = favicon ? favicon.getAttribute("href") : null;

    // if (favicon && metaImage) {
    //   favicon.setAttribute("href", metaImage);
    // }

    return () => {
      // if (favicon && prevFavicon !== null) {
      //   favicon.setAttribute("href", prevFavicon);
      // }

      document.title = prevTitle;
    };
  }, [blog]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white text-gray-500">
        <Loader2 className="h-6 w-6 animate-spin mr-2" />
        Loading article…
      </div>
    );
  }

  if (notFound || !blog) {
    return <Navigate to="/resources/blogs" replace />;
  }

  const title = blog.blog_title;
  const category = blog.blog_category;
  const date = formatDate(blog.blog_date || blog.created_at);
  const readTime = readTimeOf(blog.blog_description);
  const image = imageUrl(blog.blog_image);
  const content = blog.blog_description;
  const tags = parseTags(blog.blog_tags);
  const shareUrl = window.location.href;
  const shareText = encodeURIComponent(title || "");

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      /* clipboard unavailable */
    }
  };

  // Open the share modal and copy the link to the clipboard right away.
  const openShare = () => {
    setShareOpen(true);
    handleCopy();
  };

  // Open the platform's post composer directly in a centered popup window.
  const openShareWindow = (e) => {
    e.preventDefault();
    const url = e.currentTarget.href;
    const w = 600;
    const h = 640;
    const left = window.screenX + Math.max(0, (window.outerWidth - w) / 2);
    const top = window.screenY + Math.max(0, (window.outerHeight - h) / 2);
    window.open(
      url,
      "sharePopup",
      `width=${w},height=${h},left=${left},top=${top}`
    );
  };

  // Paginate the related articles.
  const relatedTotalPages = Math.max(
    1,
    Math.ceil(related.length / RELATED_PAGE_SIZE)
  );
  const relatedCurrentPage = Math.min(relatedPage, relatedTotalPages);
  const pagedRelated = related.slice(
    (relatedCurrentPage - 1) * RELATED_PAGE_SIZE,
    relatedCurrentPage * RELATED_PAGE_SIZE
  );

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative pt-32 pb-16 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage:
                "radial-gradient(circle at 2px 2px, white 1px, transparent 0)",
              backgroundSize: "40px 40px",
            }}
          />
        </div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          {/* Back Link */}
          <Link
            to="/resources/blogs"
            className="inline-flex items-center text-gray-400 hover:text-white mb-8 mr-6 transition-colors"
            data-testid="back-to-blogs"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to all articles
          </Link>

          {/* Category Badge */}
          {category && (
            <span className="inline-block px-4 py-1.5 bg-[#b32126] text-white rounded-full text-sm font-semibold mb-6">
              {category}
            </span>
          )}

          {/* Title */}
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight">
            {title}
          </h1>

          {/* Meta Info */}
          <div className="flex flex-wrap items-center gap-6 text-gray-300">
            {date && (
              <div className="flex items-center">
                <Calendar className="h-5 w-5 mr-2" />
                <span>{date}</span>
              </div>
            )}
            <div className="flex items-center">
              <Clock className="h-5 w-5 mr-2" />
              <span>{readTime}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Image */}
      {image && (
        <section className="relative -mt-8">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="rounded-xl sm:rounded-2xl overflow-hidden shadow-xl sm:shadow-2xl bg-gray-100">
              {/* Mobile: show the full image (auto height, contain).
                  Larger screens: a graduated hero height filled with cover. */}
              <img
                src={image}
                alt={title}
                className="w-full h-auto object-contain"
              />
            </div>
          </div>
        </section>
      )}

      {/* Blog Content */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 min-w-0">
          <article
            className="ck-content text-lg leading-relaxed text-gray-700 [overflow-wrap:anywhere] [display:flow-root]
              [&_h1]:text-4xl [&_h1]:font-bold [&_h1]:text-gray-900 [&_h1]:mt-10 [&_h1]:mb-4
              [&_h2]:text-3xl [&_h2]:font-bold [&_h2]:text-gray-900 [&_h2]:mt-12 [&_h2]:mb-4
              [&_h3]:text-2xl [&_h3]:font-semibold [&_h3]:text-gray-900 [&_h3]:mt-8 [&_h3]:mb-3
              [&_h4]:text-xl [&_h4]:font-semibold [&_h4]:text-gray-900 [&_h4]:mt-6 [&_h4]:mb-2
              [&_p]:my-4
              [&_p:has(>br:only-child)]:my-0 [&_p:has(>br:only-child)]:leading-tight
              [&_ul]:pl-6 [&_ol]:pl-6
              [&_a]:text-[#b32126] [&_a]:font-medium [&_a]:underline
              [&_strong]:text-gray-900
              [&_img]:max-w-full [&_img]:h-auto
              [&_video]:max-w-full [&_video]:h-auto [&_video]:rounded-lg
              [&_iframe]:w-full [&_iframe]:max-w-full [&_iframe]:aspect-video [&_iframe]:rounded-lg
              [&_.table]:overflow-x-auto"
            dangerouslySetInnerHTML={{ __html: content }}
          />

        </div>
      </section>

      {/* Tags */}
      {tags.length > 0 && (
        <section className="pb-8">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-wrap gap-2">
              {tags.map((tag) => (
                <span
                  key={tag}
                  className="rounded-full bg-gray-100 px-3 py-1 text-sm font-medium text-gray-900"
                >
                  #{tag}
                </span>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Share */}
      <section className="pb-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <button
            type="button"
            onClick={openShare}
            className="inline-flex items-center gap-2 font-semibold text-gray-900 hover:underline"
          >
            <Share2 className="h-4 w-4" />
            Share this article
          </button>
        </div>
      </section>

      {/* Share modal */}
      {shareOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
          onClick={() => setShareOpen(false)}
        >
          <div
            className="relative w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setShareOpen(false)}
              className="absolute right-4 top-4 text-gray-400 hover:text-gray-700"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>

            <h3 className="mb-4 text-lg font-bold text-gray-900">
              Sharing link
            </h3>

            {/* Copy link */}
            <div className="flex items-stretch overflow-hidden rounded-lg border border-gray-200">
              <span className="flex flex-1 items-center gap-2 truncate bg-gray-50 px-3 text-sm text-gray-700">
                <Link2 className="h-4 w-4 shrink-0 text-gray-400" />
                <span className="truncate">{shareUrl}</span>
              </span>
              <button
                type="button"
                onClick={handleCopy}
                className="inline-flex shrink-0 items-center gap-1.5 bg-[#b32126] px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#8f1a1f]"
              >
                {copied ? (
                  <>
                    <Check className="h-4 w-4" />
                    Copied
                  </>
                ) : (
                  "Copy link"
                )}
              </button>
            </div>
            <p className="mt-2 text-xs text-gray-500">Copy this link</p>

            {/* Or share via */}
            <div className="mt-5 flex items-center gap-3">
              <span className="text-sm text-gray-500">Or share via</span>
              <a
                href={`https://twitter.com/intent/tweet?text=${shareText}&url=${shareUrl}`}
                target="_blank"
                rel="noopener noreferrer"
                className="rounded-lg border border-gray-200 p-2.5 text-gray-600 transition-colors hover:border-[#b32126] hover:bg-[#b32126] hover:text-white"
                onClick={openShareWindow}
                aria-label="Share on X"
              >
                <svg viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5" aria-hidden="true">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                </svg>
              </a>
              <a
                href={`https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`}
                target="_blank"
                rel="noopener noreferrer"
                className="rounded-lg border border-gray-200 p-2.5 text-gray-600 transition-colors hover:border-[#b32126] hover:bg-[#b32126] hover:text-white"
                onClick={openShareWindow}
                aria-label="Share on Facebook"
              >
                <Facebook className="h-5 w-5" />
              </a>
              <a
                href={`https://www.linkedin.com/sharing/share-offsite/?url=${shareUrl}`}
                target="_blank"
                rel="noopener noreferrer"
                className="rounded-lg border border-gray-200 p-2.5 text-gray-600 transition-colors hover:border-[#b32126] hover:bg-[#b32126] hover:text-white"
                onClick={openShareWindow}
                aria-label="Share on LinkedIn"
              >
                <Linkedin className="h-5 w-5" />
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Related Articles */}
      {related.length > 0 && (
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mb-10 text-center">
              <span className="text-[#b32126] font-semibold tracking-wide text-sm uppercase">
                Keep Reading
              </span>
              <h2 className="mt-2 text-2xl md:text-3xl font-bold text-gray-900">
                Related Articles
              </h2>
            </div>
            <div className="grid md:grid-cols-3 gap-8">
              {pagedRelated.map((relatedBlog) => (
                <Link
                  key={relatedBlog.id}
                  to={`/resources/blogs/${relatedBlog.blog_slug}`}
                  className="group"
                >
                  <article className="h-full flex flex-col bg-white rounded-2xl shadow-sm overflow-hidden hover:shadow-xl hover:-translate-y-1 transition-all duration-300 border border-gray-100">
                    <div className="relative h-48 overflow-hidden bg-gray-100">
                      <img
                        src={imageUrl(relatedBlog.blog_image)}
                        alt={relatedBlog.blog_title}
                        className="w-full h-full object-containt transition-transform duration-500 group-hover:scale-105"
                      />
                      <div className="absolute top-4 left-4">
                        <span className="bg-white/95 backdrop-blur text-gray-700 text-xs font-semibold px-3 py-1.5 rounded-full shadow-sm">
                          {relatedBlog.blog_category}
                        </span>
                      </div>
                    </div>
                    <div className="p-6 flex flex-col flex-1">
                      <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-[#b32126] transition-colors line-clamp-2">
                        {relatedBlog.blog_title}
                      </h3>
                      <p className="text-gray-600 text-sm line-clamp-2 mb-4 flex-1">
                        {stripHtml(relatedBlog.blog_description).slice(0, 120)}
                      </p>
                      <div className="flex items-center justify-between text-sm text-gray-500 pt-4 border-t border-gray-100">
                        <span className="flex items-center">
                          <Calendar className="h-4 w-4 mr-1.5" />
                          {formatDate(
                            relatedBlog.blog_date || relatedBlog.created_at
                          )}
                        </span>
                        <span className="flex items-center">
                          <Clock className="h-4 w-4 mr-1.5" />
                          {readTimeOf(relatedBlog.blog_description)}
                        </span>
                      </div>
                    </div>
                  </article>
                </Link>
              ))}
            </div>

            {/* Related pagination */}
            {relatedTotalPages > 1 && (
              <div className="mt-10 flex items-center justify-center gap-1">
                <button
                  type="button"
                  disabled={relatedCurrentPage === 1}
                  onClick={() => setRelatedPage(relatedCurrentPage - 1)}
                  className="inline-flex items-center gap-1 rounded-md px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:hover:bg-transparent transition-colors"
                >
                  <ChevronLeft className="h-4 w-4" />
                  Prev
                </button>
                {Array.from({ length: relatedTotalPages }, (_, i) => i + 1).map(
                  (p) => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => setRelatedPage(p)}
                      className={`h-10 w-10 rounded-md text-sm font-medium transition-colors ${
                        p === relatedCurrentPage
                          ? "bg-[#b32126] text-white shadow"
                          : "text-gray-600 hover:bg-gray-100"
                      }`}
                    >
                      {p}
                    </button>
                  )
                )}
                <button
                  type="button"
                  disabled={relatedCurrentPage === relatedTotalPages}
                  onClick={() => setRelatedPage(relatedCurrentPage + 1)}
                  className="inline-flex items-center gap-1 rounded-md px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:hover:bg-transparent transition-colors"
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Common Sections */}
      <WhatWeDoSection />
      <IndustriesSection />
      <EngagementModelsSection bgColor="bg-white" />
      <TechnologiesSection />
      <FAQSection />

      {/* CTA Section */}
      <section className="py-16 bg-[#b32126]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Start Your Project?
          </h2>
          <p className="text-xl text-red-100 mb-8 max-w-2xl mx-auto">
            Let's discuss how we can help bring your ideas to life.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center px-8 py-4 bg-white text-[#b32126] font-semibold rounded-xl hover:bg-gray-100 transition-all shadow-lg"
            data-testid="cta-contact"
          >
            Get In Touch
            <ArrowLeft className="ml-2 h-5 w-5 rotate-180" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default BlogDetailPage;
